/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, Fragment, Suspense } from "react";
import { Outlet, RouteObject, useRoutes } from "react-router-dom";
import Home from "./containers/home/Home";
import Login from "./containers/auth/Login";
import NotFound from "./components/NotFound";
import Layout from "./containers/layouts/Layout";
import AuthGuard from "./contexts/AuthGuard";
import GuestGuard from "./contexts/GuestGuard";
import ExternaLogin from "./containers/auth/ExternaLogin";
import LoadingScreen from "./components/LoadingScreen";
import { PageMeta } from "./components/PageMeta";
import { Permission } from "./features/accounts/account";
import { BasicLayout } from "./containers/layouts/BasicLayout";
import AppHealth from "./containers/home/Health";
import { EmptyLayout } from "./containers/layouts/EmptyLayout";
import { RouteTable } from "./ui/routes";
import { DialogProvider } from "./contexts/DialogContext";
import ErrorBoundary from "./contexts/ErrorBoundary";
import { PageLogger } from "./contexts/PageLogger";
import Signup from "./containers/auth/Signup";
import { JourneyProvider } from "./containers/start/JourneyProvider";
import QuickGuide from "./containers/start/QuickGuide";
// Every route below is code-split, so every route can hit CM2-14G when a browser
// holds a stale index.html. Aliased so the route table reads unchanged.
import { lazyWithRetry as lazy } from "./services/lazyWithRetry";

type RouteDef = {
  path?: string;
  title?: string;
  children?: RouteDef[];
  guard?: any;
  guardParams?: any;
  layout?: any;
  component?: any;
  caseSensitive?: boolean;
  index?: boolean;
  loading?: string;
  permission?: Permission;
};

const productLoadMessage = "loading product database...";

const AppLayout = () => (
  <ErrorBoundary>
    <DialogProvider>
      <JourneyProvider>
        <QuickGuide />
        <Outlet />
      </JourneyProvider>
    </DialogProvider>
  </ErrorBoundary>
);

const routeDefs: RouteDef[] = [
  {
    layout: AppLayout,
    children: [
      {
        path: "/login",
        title: "Sign In",
        component: Login,
        guard: GuestGuard,
      },
      {
        path: "/signup",
        title: "Sign Up",
        component: Signup,
        guard: GuestGuard,
      },
      {
        path: "/forgot-password",
        title: "Forgot Password",
        component: lazy(() => import("./containers/auth/ForgotPassword")),
        guard: GuestGuard,
      },
      {
        path: "/reset-password",
        title: "Reset Password",
        component: lazy(() => import("./containers/auth/ResetPassword")),
        guard: GuestGuard,
      },
      {
        // S71 — gated on `lgn` (roles `login` or `siteadmin`), which is what the server actually
        // enforces here (AdminLoginAsync -> User.CanImpersonate). It was `su`, which silently
        // locked out every `login`-only holder once S71 stopped `crm` implying superuser.
        path: "/admin-login",
        title: "Sign In",
        component: lazy(() => import("./containers/auth/AdminLogin")),
        guard: AuthGuard,
        guardParams: { lgn: true },
      },
      {
        // S71 — the support console. Guarded on `sup` (roles `crm` or `siteadmin`), which is
        // strictly narrower than the `su` gate on /siteadmin below.
        path: "/support",
        component: lazy(() => import("./containers/support/SupportLayout")),
        guard: AuthGuard,
        guardParams: { sup: true },
        children: [
          {
            index: true,
            title: "Support",
            component: lazy(() => import("./containers/support/SupportSearch")),
          },
          {
            // Static, so it wins over `:id` below on the router's own ranking.
            path: "notes",
            title: "Support - Notes",
            component: lazy(
              () => import("./containers/support/SupportNotesFeed"),
            ),
          },
          {
            // S114 Ph E2. Static, like `notes`, so it wins over `:id`.
            path: "report",
            title: "Support - Report",
            component: lazy(() => import("./containers/support/SupportReport")),
          },
          {
            // S91 Ph E. Static, like `notes`, so it wins over `:id`.
            path: "attention",
            title: "Support - Needs attention",
            component: lazy(
              () => import("./containers/support/SupportAttention"),
            ),
          },
          {
            path: ":id",
            title: "Support - Account",
            component: lazy(
              () => import("./containers/support/SupportAccountPage"),
            ),
          },
        ],
      },
      {
        path: "/siteadmin",
        component: lazy(() => import("./containers/layouts/SiteAdminLayout")),
        guard: AuthGuard,
        guardParams: { su: true },
        children: [
          {
            index: true,
            title: "Site Admin",
            component: lazy(() => import("./containers/siteadmin/Dashboard")),
          },
          {
            path: "accounts",
            title: "Account Intelligence",
            component: lazy(
              () => import("./containers/siteadmin/accounts/AccountList"),
            ),
          },
          {
            path: "accounts/:id",
            title: "Account Detail",
            component: lazy(
              () => import("./containers/siteadmin/accounts/AccountDetail"),
            ),
          },
          {
            path: "unpaid",
            title: "Unpaid",
            component: lazy(
              () => import("./containers/siteadmin/unpaid/UnpaidWorkbench"),
            ),
          },
          {
            path: "system-digest",
            title: "System Digest",
            component: lazy(
              () => import("./containers/siteadmin/SystemDigest"),
            ),
          },
          {
            path: "copilot-lab/:conversationId?",
            title: "AI Copilot Lab",
            component: lazy(
              () => import("./containers/siteadmin/copilot/CopilotLab"),
            ),
          },
          {
            path: "knowledge",
            title: "Knowledge Base",
            component: lazy(
              () => import("./containers/siteadmin/copilot/KnowledgeManager"),
            ),
          },
          {
            path: "copilot",
            title: "Copilot Analytics",
            component: lazy(
              () =>
                import("./containers/siteadmin/copilot/CopilotConversationViewer"),
            ),
          },
          {
            path: "copilot/:conversationId",
            title: "Conversation Detail",
            component: lazy(
              () =>
                import("./containers/siteadmin/copilot/CopilotConversationDetail"),
            ),
          },
          {
            path: "concierge",
            title: "Concierge Analytics",
            component: lazy(
              () =>
                import("./containers/siteadmin/concierge/ConciergeSessionViewer"),
            ),
          },
          {
            path: "concierge/:sessionId",
            title: "Concierge Session Detail",
            component: lazy(
              () =>
                import("./containers/siteadmin/concierge/ConciergeSessionDetail"),
            ),
          },
          // Add more admin routes here as needed
        ],
      },
      {
        path: "/external-login",
        component: ExternaLogin,
        layout: BasicLayout,
      },
      {
        path: "/v1-login",
        component: lazy(() => import("./containers/auth/V1Login")),
        layout: BasicLayout,
      },
      {
        path: "/join",
        title: "Join Account",
        component: lazy(() => import("./containers/account/Join")),
        guard: GuestGuard,
      },
      {
        path: "/shopify-connect",
        title: "Connecting Shopify",
        component: lazy(
          () => import("./containers/auth/ShopifyConnectHandler"),
        ),
      },
      {
        path: "/health-app",
        component: AppHealth,
      },
      {
        path: "/",
        component: Layout,
        guard: AuthGuard,
        children: [
          {
            index: true,
            component: Home,
            title: "Dashboard",
            loading: "loading dashboard...",
          },
          {
            path: "/account/:tab?/:subtab?",
            title: "Account",
            component: lazy(() => import("./containers/account/Account")),
            loading: "loading account...",
          },
          // {
          //   path: "/start",
          //   title: "Start",
          //   component: lazy(() => import("./containers/account/Start")),
          // },
          {
            path: "/products/categories",
            title: "Categories",
            guard: AuthGuard,
            permission: "products",
            component: lazy(
              () => import("./containers/products/categories/CategoryListView"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/products/collections",
            title: "Collections",
            guard: AuthGuard,
            permission: "products",
            component: lazy(
              () => import("./containers/products/collections/CollectionPage"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/products",
            title: "Products",
            component: lazy(
              () => import("./containers/products/grid/ProductMainGrid"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/products/catalog-mapping",
            title: "Product-Catalog Mapping",
            permission: "products",
            component: lazy(
              () =>
                import("./containers/products/mapping/ProductCatalogMappingPage"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/products/upload",
            title: "Upload Products",
            permission: "products",
            component: lazy(
              () => import("./containers/products/upload/BulkProductUploader"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/products/inventory",
            title: "Inventory",
            component: lazy(
              () => import("./containers/products/inventory/InventoryList"),
            ),
            loading: "loading inventory...",
          },
          {
            path: "/import/shopify",
            title: "Shopify Import",
            permission: "products",
            component: lazy(
              () =>
                import("./containers/products/import/shopify/ShopifyImport"),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/import/woocommerce",
            title: "WooCommerce Import",
            permission: "products",
            component: lazy(
              () =>
                import(
                  "./containers/products/import/woocommerce/WooCommerceImport"
                ),
            ),
            loading: productLoadMessage,
          },
          {
            path: "/import/shopify/collections",
            title: "Shopify Collections",
            permission: "products",
            component: lazy(
              () =>
                import(
                  "./containers/products/import/shopify/ShopifyCollections"
                ),
            ),
            loading: "loading collections...",
          },
          {
            path: "/concierge/:sessionId?",
            title: "AI Concierge",
            permission: "products",
            component: lazy(
              () => import("./containers/copilot/concierge/ConciergePage"),
            ),
            loading: "loading AI Concierge...",
          },
          {
            // S70 Ph B — customer analytics. In the NavBar since 2026-10-04.
            path: "/analytics",
            title: "Analytics",
            component: lazy(() => import("./containers/analytics/Analytics")),
            loading: "loading your analytics...",
          },
          {
            path: "/showrooms",
            title: "Showroom",
            component: lazy(() => import("./containers/showroom/ShowroomList")),
            loading: productLoadMessage,
          },
          {
            path: "/orders",
            title: "Orders",
            component: lazy(() => import("./containers/orders/OrdersList")),
            loading: "loading orders...",
          },
          {
            path: "/orders/new",
            title: "Create New Order",
            component: lazy(() => import("./containers/orders/OrderForm")),
            loading: "loading order form...",
          },
          {
            path: "/orders/settings",
            title: "Order Settings",
            component: lazy(() => import("./containers/orders/OrderSettings")),
            loading: "loading order settings...",
          },
          // {
          //   path: "/orders/payments",
          //   title: "Order Payments",
          //   component: lazy(
          //     () => import("./containers/orders/PaymentSettings")
          //   ),
          //   loading: "loading order payments...",
          // },
          {
            path: "/orders/:id/edit",
            title: "Edit Order",
            component: lazy(() => import("./containers/orders/OrderForm")),
            loading: "loading order form...",
          },
          {
            path: "/orders/:id",
            title: "Order Details",
            component: lazy(() => import("./containers/orders/OrderDetails")),
            loading: "loading order details...",
          },

          {
            path: "/images",
            title: "Images",
            component: lazy(() => import("./containers/images")),
            loading: "loading image database...",
          },
          {
            path: "/catalogs",
            title: "Catalogs",
            component: lazy(() => import("./containers/catalogs/CatalogList")),
          },
          {
            path: "/catalogs/create",
            title: "New Catalog",
            permission: "catalogs",
            component: lazy(
              () => import("./containers/catalogs/create/CreateCatalog"),
            ),
          },
          {
            path: "/catalogs/templates",
            title: "Catalog Templates",
            component: lazy(
              () => import("./containers/catalogs/create/StartTemplates"),
            ),
          },
          {
            path: "/catalogs/pdf-import",
            title: "PDF Import",
            permission: "catalogs",
            component: lazy(
              () => import("./containers/catalogs/pdf/PdfImportPage"),
            ),
          },
          {
            path: "/parameters",
            title: "Parameters",
            component: lazy(() => import("./containers/parameters/Parameters")),
          },
          {
            path: "/parameters/:paramId",
            title: "Parameters",
            component: lazy(() => import("./containers/parameters/Parameters")),
          },
          {
            path: "/automation",
            title: "API & Automation",
            component: lazy(() => import("./containers/automation/Automation")),
          },
          {
            path: "/pg",
            title: "Playground",
            component: lazy(() => import("./containers/playground")),
          },
          {
            path: "/pg/editor",
            title: "Playground Editor",
            component: lazy(
              () => import("./containers/playground/CustomEditor"),
            ),
          },
        ],
      },
      {
        path: "/start",
        title: "Start",
        component: lazy(() => import("./containers/start/Start")),
        guard: AuthGuard,
      },
      {
        path: "/products/add",
        title: "Get Products",
        permission: "products",
        component: lazy(() => import("./containers/products/add")),
        loading: productLoadMessage,
      },
      {
        path: "/showrooms/:storeId",
        title: "Showroom",
        component: lazy(() => import("./containers/showroom/ShowroomEditor")),
        loading: productLoadMessage,
      },
      {
        path: "/design",
        component: EmptyLayout,
        guard: AuthGuard,
        permission: "catalogs",
        children: [
          {
            title: "Designer Playground",
            index: true,
            component: lazy(
              () => import("./containers/playground/DesignerPlayground"),
            ),
          },
          {
            title: "Catalog Designer",
            path: RouteTable.catalogDesign + "/:catalogId",
            component: lazy(
              () => import("./containers/designer/catalogs/CatalogDesigner"),
            ),
          },
          {
            title: "Product Card Designer",
            path: RouteTable.cards,

            component: lazy(
              () =>
                import("./containers/designer/templates/TemplateDesignerPage"),
            ),
          },

          {
            title: "Layout Designer",
            path: RouteTable.layouts,

            component: lazy(
              () => import("./containers/designer/layouts/LayoutDesigner"),
            ),
          },
        ],
      },
      {
        path: "*",
        title: "Not Found",
        component: NotFound,
        layout: BasicLayout,
      },
    ],
  },
];

const buildRoutes = (defs: RouteDef[]): RouteObject[] => {
  return defs.map((d) => {
    const Guard = d.guard ?? Fragment;
    const Component = d.component ?? Fragment;
    const Layout = d.layout ?? Fragment;
    const gp = d.guard ? { ...d.guardParams, permission: d.permission } : {};
    const r = {
      path: d.path,
      element: (
        <>
          <PageLogger />
          <Guard {...gp}>
            {d.title && <PageMeta title={d.title} />}
            <Layout>
              <Suspense
                fallback={<LoadingScreen title={d.loading || "loading data"} />}
              >
                <Component />{" "}
              </Suspense>
            </Layout>
          </Guard>
        </>
      ),

      index: d.index,
      caseSensitive: d.caseSensitive,
    };
    return d.index
      ? { ...r, index: true }
      : { ...r, index: false, children: d.children && buildRoutes(d.children) };
  });
};

export const AppRoutes: FC = () => {
  const routes = buildRoutes(routeDefs);
  const element = useRoutes(routes);

  return <>{element}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const createAppRoutes = () => {
  const routes = buildRoutes(routeDefs);
  return routes;
};

export default AppRoutes;
